import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { ACTION_BUTTON, SURFACE_CARD, plural } from "../styles";

type BulkActionBarProps = {
  selectedCount: number;
  selectedQuotas: number;
  isProcessing: boolean;
  onClear: () => void;
  onAccept: () => void;
  onReject: () => void;
};

/**
 * Barra flutuante de ações em lote.
 *
 * Fica sempre montada para que a entrada e a saída sejam animadas apenas com
 * classes do Tailwind — sem biblioteca de animação.
 *
 * Hierarquia: "Aceitar" é a ação primária (preenchida). "Rejeitar" é
 * destrutiva mas secundária (contorno), e "Limpar" é neutra.
 */
export const BulkActionBar = ({
  selectedCount,
  selectedQuotas,
  isProcessing,
  onClear,
  onAccept,
  onReject,
}: BulkActionBarProps) => {
  const isVisible = selectedCount > 0;

  return (
    <div
      aria-hidden={!isVisible}
      className={`fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3 transition-all duration-200 ease-out sm:bottom-6 sm:pb-0 ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-6 opacity-0"
      }`}
    >
      <div
        className={`flex w-full flex-col gap-3 rounded-xl border p-3 shadow-lg sm:w-auto sm:flex-row sm:items-center sm:gap-4 sm:px-4 ${SURFACE_CARD}`}
      >
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground tabular-nums">
            {selectedCount}
          </span>{" "}
          {plural(selectedCount, "selecionada", "selecionadas")}
          <span className="mx-1.5 opacity-50">·</span>
          <span className="font-semibold text-foreground tabular-nums">
            {selectedQuotas}
          </span>{" "}
          {plural(selectedQuotas, "cota", "cotas")}
        </p>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={isProcessing}
            onClick={onClear}
            className="flex-1 text-muted-foreground hover:text-foreground sm:flex-none"
          >
            Limpar
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={isProcessing}
            onClick={onReject}
            className={`flex-1 sm:flex-none ${ACTION_BUTTON.rejectOutline}`}
          >
            <X className="mr-1.5 h-4 w-4" />
            Rejeitar
          </Button>
          <Button
            size="sm"
            disabled={isProcessing}
            onClick={onAccept}
            className={`flex-1 sm:flex-none ${ACTION_BUTTON.acceptSolid}`}
          >
            <Check className="mr-1.5 h-4 w-4" />
            Aceitar
          </Button>
        </div>
      </div>
    </div>
  );
};
